import { useQuery } from 'data-fetching-client';
import { useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { debounce } from 'marketplace-ui-common/utils/debounce';
import { GENERATE_REVIEW_TITLE } from '../queries/generateReviewTitleQuery';
export function useGeneratedReviewTitle({
  answers,
  entityName,
  reviewType
}) {
  const [refetching, setRefetching] = useState(false);
  const [initialRequest, setInitialRequest] = useState(true);
  const [formattedAnswers, setFormattedAnswers] = useState(structuredClone(Object.assign({}, answers, {
    TITLE: undefined
  })));
  const hasAnswers = !Object.values(formattedAnswers).every(value => !value);
  const [skip, setSkip] = useState(!hasAnswers);
  const prevAnswers = usePrevious(formattedAnswers);
  const {
    data,
    error,
    loading,
    refetch
  } = useQuery(GENERATE_REVIEW_TITLE, {
    variables: {
      answers: formattedAnswers,
      entityName,
      reviewType
    },
    skip,
    onCompleted: () => {
      setSkip(false);
    }
  });

  // Debounces updating the answers object to avoid sending excessive requests
  // Otherwise the useQuery hook will send a request for each change
  const debouncedUpdateAnswers = useMemo(() => debounce(updates => {
    setFormattedAnswers(updates);
    setInitialRequest(false);
    setSkip(true);
  }, 1000), []);

  // Debounces the refetch calls to avoid excessive requests
  const debouncedRefetch = useMemo(() => debounce(() => {
    refetch().then(() => {
      setRefetching(false);
    }).catch(() => {
      // pass through
    });
  }, 1000), [refetch]);

  // Handles updating answers
  useEffect(() => {
    const answersWithoutTitle = structuredClone(answers);
    delete answersWithoutTitle.TITLE;
    if (initialRequest) {
      // Only use the debounced answer updates if the call is made by the useQuery hook and not the refetch
      debouncedUpdateAnswers(answersWithoutTitle);
    } else {
      setFormattedAnswers(answersWithoutTitle);
    }
  }, [answers, debouncedUpdateAnswers, initialRequest, skip]);

  // Handles sending refetch requests
  useEffect(() => {
    let shouldRefetch = false;
    if (formattedAnswers && prevAnswers) {
      Object.keys(formattedAnswers).forEach(key => {
        if (formattedAnswers[key] !== prevAnswers[key]) {
          shouldRefetch = true;
        }
      });
    }
    if (shouldRefetch && hasAnswers) {
      setRefetching(true);
      debouncedRefetch();
    }
  }, [debouncedRefetch, formattedAnswers, hasAnswers, skip, prevAnswers]);
  return {
    generatedTitle: data && !error ? data.title.title : undefined,
    loading: loading || refetching
  };
}